import { Group, Loader, Text } from '@mantine/core';
import { useIsFirstRender } from '@mantine/hooks';
import { IconCheck } from '@tabler/icons-react';
import { tKeys, useTranslation } from 'locales/index';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';

type LoadingSuccessIndicatorProps = {
  children?: React.ReactNode;
  /** Use this to hide the spinner on fast requests to avoid excessive jitter */
  inhibitLoadingState?: boolean;
  isLoading: boolean;
};

export const LoadingSuccessIndicator = ({
  children,
  inhibitLoadingState = false,
  isLoading,
}: LoadingSuccessIndicatorProps) => {
  const [shouldShowSuccessState, setShouldShowSuccessState] = useState(false);
  const isFirstRender = useIsFirstRender();

  useEffect(() => {
    if (isLoading || isFirstRender) return;
    setShouldShowSuccessState(true);
    setTimeout(() => setShouldShowSuccessState(false), 1500);
  }, [isLoading]);

  return (
    <AnimatePresence>
      {(() => {
        if (isLoading && !inhibitLoadingState)
          return (
            <motion.div {...ANIMATION_PROPS}>
              <Loader size="xs" />
            </motion.div>
          );

        if (shouldShowSuccessState)
          return <motion.div {...ANIMATION_PROPS}>{children ?? <SavedIndicator />}</motion.div>;

        return null;
      })()}
    </AnimatePresence>
  );
};

const ANIMATION_PROPS = { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } };

const SavedIndicator = () => {
  const { t } = useTranslation();

  return (
    <Group align="end" gap={4}>
      <IconCheck size={18} color="green" />
      <Text c="green" fs="initial" size="xs">
        {t(tKeys.SAVED)}
      </Text>
    </Group>
  );
};
